'use client';

import { useId, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';
import { localizedHref } from '~/lib/marketing/routes';

import {
  SOLUTIONS,
  SOLUTIONS_SECTION,
  type Solution,
} from '../../_lib/solutions.content';
import { LIGHT_LOGO_FILES } from '../../_lib/home.content';
import { TESTIMONIALS } from '../../_lib/testimonials.content';
import { AudienceVisual } from '../audience-visual';
import { SOLUTION_ICONS } from '../solutions-menu';

const LOGO_BASE = '/landing-pages/flow/customers';
const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * "Who it is for" tabs: one panel per audience with an editorial photo, a
 * live demo of that audience's key flow, the four things that matter to them,
 * a client quote when we have one, and a deep link to the audience page.
 */
export function SolutionsTabs({ locale }: { locale: MarketingLocale }) {
  const isRTL = locale === 'ar';
  const baseId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // `switched` gates the panel entrance so the first panel paints straight
  // from the server HTML instead of waiting for hydration at opacity 0.
  const [{ active, switched }, setTabs] = useState({ active: 0, switched: false });
  const setActive = (index: number) => setTabs({ active: index, switched: true });
  const solution = SOLUTIONS[active]!;

  const focusTab = (index: number) => {
    const next = (index + SOLUTIONS.length) % SOLUTIONS.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const forward = isRTL ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRTL ? 'ArrowRight' : 'ArrowLeft';

    if (event.key === forward) {
      event.preventDefault();
      focusTab(active + 1);
    } else if (event.key === backward) {
      event.preventDefault();
      focusTab(active - 1);
    }
  };

  return (
    <section
      id="solutions"
      className="scroll-mt-16 bg-[#ede8e2] px-4 py-16 md:px-8 md:py-24 lg:px-[90px] lg:py-[120px]"
    >
      <div className="mx-auto max-w-[1320px]">
        <div className="mx-auto max-w-[760px] text-center">
          <p className="mb-4 font-roboto-mono text-[12px] uppercase tracking-wide text-[#202020]/80 md:text-[14px]">
            {SOLUTIONS_SECTION.label[locale]}
          </p>
          <h2
            className="font-roboto text-[30px] leading-[1.12] text-[#202020] md:text-[40px] lg:text-[48px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {SOLUTIONS_SECTION.heading[locale]}
          </h2>
          <p className="mt-4 font-roboto text-[16px] leading-relaxed text-[#2b2b2b]/70 md:text-[18px]">
            {SOLUTIONS_SECTION.description[locale]}
          </p>
        </div>

        <div className="mt-10 flex justify-center md:mt-12">
          <div
            role="tablist"
            aria-label={SOLUTIONS_SECTION.label[locale]}
            onKeyDown={onKeyDown}
            className="-mx-4 flex max-w-[calc(100%+2rem)] gap-1 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:max-w-full md:rounded-full md:bg-white/70 md:p-1.5 md:ring-1 md:ring-[#dcd7cf]"
          >
            {SOLUTIONS.map((item, index) => {
              const selected = index === active;
              const Icon = SOLUTION_ICONS[item.slug];

              return (
                <button
                  key={item.slug}
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`${baseId}-tab-${index}`}
                  aria-selected={selected}
                  aria-controls={`${baseId}-panel`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(index)}
                  className={cn(
                    'relative inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 font-roboto text-[14px] font-medium transition-colors md:px-5 md:text-[15px]',
                    selected ? 'text-white' : 'text-[#2b2b2b]/70 hover:text-[#202020]',
                  )}
                >
                  {selected && (
                    <motion.span
                      layoutId={`${baseId}-pill`}
                      className="absolute inset-0 rounded-full bg-[#202020]"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  <Icon className="relative h-4 w-4" />
                  <span className="relative whitespace-nowrap">{item.tabLabel[locale]}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          role="tabpanel"
          id={`${baseId}-panel`}
          aria-labelledby={`${baseId}-tab-${active}`}
          className="mt-10 md:mt-14"
        >
          <motion.div
            key={solution.slug}
            initial={switched ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-16"
          >
            <SolutionCopy solution={solution} locale={locale} />
            <div className="order-1 lg:order-2">
              <AudienceVisual
                image={solution.image}
                imageAlt={solution.imageAlt[locale]}
                demo={solution.demo}
                locale={locale}
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

function SolutionCopy({
  solution,
  locale,
}: {
  solution: Solution;
  locale: MarketingLocale;
}) {
  const testimonial = solution.testimonial ? TESTIMONIALS[solution.testimonial] : null;

  return (
    <div className="order-2 lg:order-1">
      <h3
        className="font-roboto text-[26px] leading-[1.15] text-[#202020] md:text-[34px]"
        style={{ fontVariationSettings: "'wdth' 100" }}
      >
        {solution.heading[locale]}
      </h3>
      <p className="mt-4 font-roboto text-[16px] leading-relaxed text-[#2b2b2b]/75 md:text-[17px]">
        {solution.description[locale]}
      </p>

      <ul className="mt-8 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
        {solution.bullets.map((bullet) => (
          <li key={bullet.title.en} className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[#202020] ring-1 ring-[#dcd7cf]">
              <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            <span>
              <span className="block font-roboto text-[15px] font-medium text-[#202020]">
                {bullet.title[locale]}
              </span>
              <span className="mt-1 block font-roboto text-[14px] leading-snug text-[#2b2b2b]/65">
                {bullet.body[locale]}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {testimonial && (
        <figure className="mt-8 border-s-2 border-[#202020] ps-5">
          <blockquote className="font-roboto text-[16px] leading-relaxed text-[#202020] md:text-[17px]">
            {locale === 'ar' ? '«' : '\u201c'}
            {testimonial.excerpt[locale]}
            {locale === 'ar' ? '»' : '\u201d'}
          </blockquote>
          <figcaption className="mt-3 flex items-center gap-3">
            <Image
              src={`${LOGO_BASE}/${testimonial.logo}`}
              alt=""
              width={64}
              height={32}
              className={cn(
                'h-7 w-auto object-contain',
                LIGHT_LOGO_FILES.has(testimonial.logo) && 'brightness-0',
              )}
            />
            <span className="font-roboto-mono text-[11px] uppercase tracking-wide text-[#8a857c]">
              {[testimonial.person?.[locale], testimonial.company]
                .filter(Boolean)
                .join(locale === 'ar' ? '، ' : ', ')}
            </span>
          </figcaption>
        </figure>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
        <Link
          href={localizedHref(solution.href, locale)}
          className="group inline-flex h-[50px] items-center gap-3 bg-[#202020] px-6 text-white transition-colors hover:bg-[#2b2b2b]"
        >
          <span className="font-roboto-mono text-[12px] uppercase">{solution.cta[locale]}</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
        </Link>
      </div>

      {solution.logos.length > 0 && (
        <div className="mt-8 border-t border-[#202020]/10 pt-6">
          <p className="mb-4 font-roboto-mono text-[11px] uppercase tracking-wide text-[#8a857c]">
            {SOLUTIONS_SECTION.clientsLabel[locale]}
          </p>
          <ul className="flex flex-wrap items-center gap-x-7 gap-y-4">
            {solution.logos.map((logo) => (
              <li key={logo.file}>
                <Image
                  src={`${LOGO_BASE}/${logo.file}`}
                  alt={logo.name}
                  width={96}
                  height={40}
                  className={cn(
                    'h-8 w-auto object-contain opacity-70 mix-blend-multiply grayscale',
                    LIGHT_LOGO_FILES.has(logo.file) && 'brightness-0',
                  )}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
